import styled from '@emotion/styled';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { usePageManager } from 'hooks/pageManager/usePageManager';
import { useSetAtom } from 'jotai';
import { MuiRTL } from 'lib/MuiRTL';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { selectedRouteTitleAtom } from 'store/private/layout/layoutAtoms';

import { isLinkActive, isSubLinkActive } from '../helpers';

const NavListItemWithChildren = ({ link, onSelectSubLink }) => {
  const [activeDropdownMenuId, setActiveDropdownMenuId] = useState('');

  const { pathname } = useLocation();

  return (
    <>
      <NavListItem
        key={link.id}
        selected={isLinkActive(link, pathname)}
        sx={{ mt: link.mt ? 2 : 0 }}
        onClick={() =>
          setActiveDropdownMenuId(
            activeDropdownMenuId === link.id ? '' : link.id,
          )
        }
      >
        <StyledListItemIcon sx={{ width: 'auto' }} color="inherit">
          {link.icon}
        </StyledListItemIcon>
        <ListItemText
          primary={link.label}
          sx={{
            marginLeft: '16px',
          }}
        />
        {activeDropdownMenuId === link.id ? <ExpandLess /> : <ExpandMore />}
      </NavListItem>
      <Collapse
        in={activeDropdownMenuId === link.id}
        timeout="auto"
        unmountOnExit
      >
        <LinksWrapper>
          <CollapsebleList component="div" disablePadding>
            {link?.subLinks?.map(subLink => (
              <CollapseListItem
                key={subLink.id}
                selected={isSubLinkActive(subLink, pathname)}
                sx={{
                  mt: link.mt ? 2 : 0,
                  p: '8px 13px !important',
                }}
                onClick={() => onSelectSubLink(subLink)}
              >
                <ListItemText primary={subLink.label} />
                {isSubLinkActive(subLink, pathname) ? (
                  <SubListItemActiveIcon />
                ) : null}
              </CollapseListItem>
            ))}
          </CollapsebleList>
        </LinksWrapper>
      </Collapse>
    </>
  );
};

export const NavLinksRenderer = params => {
  const { links } = params;
  const { openPage } = usePageManager();
  const { pathname } = useLocation();

  const setSelectedRouteTitle = useSetAtom(selectedRouteTitleAtom);

  const onSelectLink = link => {
    setSelectedRouteTitle(link.label);

    openPage({
      href: link.href,
      label: link.label,
      element: link.element,
      path: link.href,
    });
  };

  const onSelectSubLink = subLink => {
    setSelectedRouteTitle(subLink.label);

    openPage({
      href: subLink.href,
      label: subLink.label,
      element: subLink.element,
      path: subLink.href,
    });
  };

  return (
    <AnimatePresence mode="wait">
      <Wrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <MuiRTL>
          <NavList
            component="nav"
            aria-label="main links"
            sx={{ width: '100%' }}
          >
            {links?.map(link =>
              link.subLinks ? (
                <NavListItemWithChildren
                  onSelectSubLink={onSelectSubLink}
                  link={link}
                  key={link.id}
                />
              ) : (
                <NavListItem
                  key={link.id}
                  selected={isLinkActive(link, pathname)}
                  sx={{ mt: link.mt ? 2 : 0 }}
                  onClick={() => onSelectLink(link)}
                >
                  <StyledListItemIcon sx={{ width: 'auto' }} color="inherit">
                    {link.icon}
                  </StyledListItemIcon>
                  <ListItemText
                    primary={link.label}
                    sx={{
                      marginLeft: '16px',
                    }}
                  />
                </NavListItem>
              ),
            )}
          </NavList>
        </MuiRTL>
      </Wrapper>
    </AnimatePresence>
  );
};

const Wrapper = styled(motion.div)`
  width: 100%;
`;

export const NavList = styled(List)`
  width: 100%;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  & .MuiListItemButton-root {
    width: 100%;
    justify-content: flex-start;
    padding: 10px 12px 10px 8px;
    border-radius: 10px;
  }
  & .MuiListItemIcon-root {
    min-width: 0;
    width: auto;
    margin-right: 0;
  }
  & .MuiSvgIcon-root {
    width: 20px;
    height: 20px;
  }
`;

export const NavListItem = styled(ListItemButton)`
  width: 100%;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border: 1px solid transparent;
  & .MuiTypography-root {
    font-size: 13px;
    font-family: 'Yekan-Regular';
  }
  & .Mui-selected {
    border-radius: 10px;
  }

  ${({ theme }) => ({
    '&.MuiListItemButton-root': {
      'marginBottom': '5px',
      'padding': '4px 8px',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '&.MuiListItemButton-root.Mui-selected': {
      'borderRadius': '10px',
      'backgroundColor': alpha('#0B7AD1', 0.28),
      'border': `1px solid ${theme.palette.layout.borderWhite}`,
      'filter': `drop-shadow(0px 3px 8px ${alpha(
        theme.palette.brand.blue.secondary,
        0.19,
      )})`,
      '& .MuiTypography-root': {
        fontSize: '13px',
        fontFamily: 'Yekan-Bold',
      },
    },
    '& .MuiTouchRipple-child': {
      color: alpha(theme.palette.brand.blue.ternary, 0.34),
    },
  })}
`;

const LinksWrapper = styled(Box)`
  width: 100%;
  display: flex;
  padding-left: 7px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CollapsebleList = styled(List)`
  width: 100%;
  padding-left: 7px;
  font-size: 13px;
  font-family: 'Yekan-Regular';
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border-left: ${({ theme }) =>
    `2px solid ${alpha(theme.palette.brand.blue.ternary, 0.24)}`};
  & .MuiListItemButton-root {
    width: 100%;
    justify-content: flex-start;
    padding: 4px 8px !important;
    border-radius: 10px;
    border: 1px solid transparent;
  }
  & .MuiSvgIcon-root {
    width: 20px;
    height: 20px;
  }

  & .MuiTypography-root {
    font-size: 13px;
    font-family: 'Yekan-Regular';
  }
  ${({ theme }) => ({
    '& .MuiListItemButton-root.Mui-selected': {
      borderRadius: '10px',
      padding: '4px 8px !important',
      backgroundColor: alpha(theme.palette.brand.blue.ternary, 0.18),
      border: `1px solid ${theme.palette.layout.borderWhite}`,
    },
    '& .MuiTouchRipple-child': {
      color: alpha(theme.palette.brand.blue.ternary, 0.34),
    },
  })}
`;

export const CollapseListItem = styled(ListItemButton)`
  width: 100%;
  margin-bottom: 2px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const StyledListItemIcon = styled(ListItemIcon)`
  color: ${({ theme }) => theme.palette.primary.contrastText};
`;

export const SubListItemActiveIcon = styled.div`
  ${({ theme }) => ({
    borderRadius: '50%',
    width: '9px',
    marginRight: '5px !important',
    height: '9px',
    backgroundColor: theme.palette.primary.contrastText,
    border: `2px solid ${alpha(theme.palette.primary.contrastText, 0.5)}`,
  })}
`;
